class ProductStickyAtc extends HTMLElement {
  // Private fields for better encapsulation
  scrollThrottle = null;
  reflectThrottle = null;
  // Guard: set while we programmatically drive the main column so the observers
  // that fire from our own writes don't bounce back and cause a sync loop.
  suppress = false;
  // Pending settle-timeout from pushToMain; cleared/replaced on each push so a
  // rapid second pick always wins instead of being dropped mid-window.
  settleTimer = null;
  // Frequency <select>s we've already bound a change listener to.
  boundFreqSelects = new WeakSet();
  boundHandlers = {};

  constructor() {
    super();
    // Cache DOM references once
    this.boundHandlers = {
      toggleDropdown: this.onToggleDropdown.bind(this),
      clickItem: this.onClickItem.bind(this),
      clickBody: this.onClickBody.bind(this),
      scroll: this.onScroll.bind(this),
      variantChange: this.scheduleReflect.bind(this),
      docChange: this.onDocumentChange.bind(this),
    };
  }

  connectedCallback() {
    // Null-safe element references
    this.form = this.querySelector('form');
    this.dropdown = this.querySelector('.subscription-dropdown');
    this.dropdownHeader = this.querySelector(
      '.subscription-dropdown__header'
    );
    this.dropdownHeaderText = this.querySelector(
      '.subscription-dropdown__header--text'
    );
    this.dropdownItems = this.querySelectorAll(
      '.subscription-dropdown__item'
    );
    this.anchorElement = document.querySelector('.product__actions');
    this.submitButton = this.querySelector('[type="submit"]');
    this.closeButton = this.querySelector(
      '.subscription-dropdown__close'
    );
    this.variantInput = this.querySelector('[name="id"]');
    this.sellingPlanInput = this.querySelector(
      '[name="selling_plan"]'
    );
    this.quantityInput = this.querySelector('[name="quantity"]');
    this.priceEachEl = this.querySelector('.js__sticky-price-each');

    // Main-column references for two-way sync. Recharge owns the purchase
    // option (Subscribe/One-Time) + frequency inside its shadow DOM; the
    // variant lives in the main add-to-cart form's #product-select.
    this.mainForm = document.getElementById('add-to-cart');
    this.mainVariantInput = this.mainForm?.querySelector('[name="id"]');

    // Localized display strings injected by the Liquid snippet (this static
    // asset can't call the `t` filter). Fall back to English if absent.
    this.labels = {
      subscribe: this.dataset.labelSubscribe || 'Subscribe',
      subscribeSave: this.dataset.labelSubscribeSave || 'Subscribe & Save',
      oneTime: this.dataset.labelOneTime || 'One-Time',
    };

    if (!this.dropdownHeader || !this.dropdownItems.length) return;

    // Event listeners with cached references
    this.dropdownHeader.addEventListener(
      'click',
      this.boundHandlers.toggleDropdown
    );
    this.dropdownItems.forEach((item) => {
      item.addEventListener('click', this.boundHandlers.clickItem);
    });
    document.addEventListener('click', this.boundHandlers.clickBody);
    // Main -> sticky: variant changes fire this event; Recharge purchase-option
    // and frequency changes are caught by the observers in watchMain().
    document.addEventListener(
      'variant:change',
      this.boundHandlers.variantChange
    );
    window.addEventListener('scroll', this.boundHandlers.scroll, {
      passive: true,
    });
    this.form.addEventListener(
      'submit',
      this.onSubmitHandler.bind(this)
    );
    this.closeButton.addEventListener(
      'click',
      this.closeContent.bind(this)
    );

    this.watchMain();
    this.scheduleReflect();
    // Initial state check
    this.checkScrollPosition();
  }

  disconnectedCallback() {
    // Cleanup all event listeners
    this.dropdownHeader?.removeEventListener(
      'click',
      this.boundHandlers.toggleDropdown
    );
    this.dropdownItems?.forEach((item) => {
      item.removeEventListener('click', this.boundHandlers.clickItem);
    });
    document.removeEventListener(
      'click',
      this.boundHandlers.clickBody
    );
    document.removeEventListener(
      'variant:change',
      this.boundHandlers.variantChange
    );
    window.removeEventListener('scroll', this.boundHandlers.scroll);
    document.removeEventListener('change', this.boundHandlers.docChange);
    clearTimeout(this.settleTimer);
    this.rechargeObserver?.disconnect();
    this.rechargeReadyObserver?.disconnect();
    this.priceObserver?.disconnect();
  }

  // Private methods (ES2022)
  onToggleDropdown(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dropdown?.classList.toggle('active');
  }

  onClickItem(e) {
    e.stopPropagation();
    // Which plan Recharge lands on isn't known until pushToMain settles, and
    // the item still carries the outgoing plan, so the per-unit line waits for
    // the reflect rather than flashing the previous selection's unit count.
    this.applyStickyItem(e.currentTarget, { deferPriceEach: true });
    this.closeContent();
    // Sticky -> main: mirror the user's sticky choice back into the main column.
    // pushToMain is reentrant, so a rapid second pick during the settle window
    // replaces the first rather than being dropped.
    this.pushToMain(e.currentTarget);
  }

  // Apply a dropdown item as the active sticky selection: header text, active
  // state, button label/availability, hidden inputs, and per-item pricing.
  // Shared by user clicks (onClickItem) and main->sticky reflection so both
  // paths stay identical.
  applyStickyItem(target, { deferPriceEach = false } = {}) {
    if (!target) return;
    // Detect purchase type from the stable structural class, not rendered copy
    // (which is localized). The <li> carries `one-time` / `subscribe`.
    const isOnetime = target.classList.contains('one-time');
    this.dropdownHeaderText.textContent = isOnetime
      ? this.labels.oneTime
      : this.labels.subscribeSave;

    this.dropdownItems.forEach((item) => {
      item.classList.toggle('active', item === target);

      const originalPrice = Number(item.dataset.price);
      // data-percent is a single ratio baked in at render time, so it can't
      // follow a Recharge tier change; prefer the allocation map, which is
      // keyed by the plan actually selected.
      const resolved = window.iwPdpPriceSync?.priceFor(
        item.dataset.value?.trim(),
        item.classList.contains('one-time')
          ? ''
          : item.dataset.sellingPlan?.trim()
      );
      const fallbackPrice = originalPrice * Number(item.dataset.percent);
      // The main buy box's quantity selector is the single source of truth for
      // how many units this add covers — pdp-price-sync drives it from the plan
      // where a plan dictates it — so the button names the same total the main
      // price line does, and the same one the cart line item will.
      const units = this.mainQuantity();
      const listPrice = Shopify.formatMoney(originalPrice * units, '');
      const displayPrice =
        (resolved?.priceCents
          ? Shopify.formatMoney(resolved.priceCents * units, '')
          : resolved?.price) || Shopify.formatMoney(fallbackPrice * units, '');

      if (item.classList.contains('active')) {
        this.renderPriceEach(deferPriceEach ? 1 : units, resolved?.price);
        if (item.dataset.available.trim() == 'true') {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.textContent = `Add to Cart | ${displayPrice}`;
          this.submitButton.setAttribute('aria-label', 'Add to cart');
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.textContent = 'Sold Out';
          this.submitButton.setAttribute('aria-label', 'Sold out');
        }

        this.variantInput.value = item.dataset.value.trim();
        this.sellingPlanInput.value = item.dataset.sellingPlan.trim();
      }

      const priceEl = item.querySelector(
        '.subscription-dropdown__item-price'
      );
      if (priceEl) {
        priceEl.innerHTML =
          displayPrice === listPrice
            ? displayPrice
            : `${displayPrice}<span>${listPrice}</span>`;
      }
    });
  }

  // Qualifies the button's shipment total, and only then — on a single-unit
  // plan the two figures are the same number and the line would just repeat it.
  renderPriceEach(units, unitPrice) {
    if (!this.priceEachEl) return;
    if (units > 1 && unitPrice) {
      this.priceEachEl.textContent = `${units} units · ${unitPrice} per unit`;
      this.priceEachEl.removeAttribute('hidden');
    } else {
      this.priceEachEl.textContent = '';
      this.priceEachEl.setAttribute('hidden', '');
    }
  }

  closeContent(e) {
    e?.stopPropagation();
    this.dropdown?.classList.remove('active');
  }

  onClickBody(e) {
    if (
      !this.dropdown.contains(e.target) &&
      !e.target.closest('[data-option], [data-value]')
    ) {
      this.dropdown.classList.remove('active');
    }
  }

  // ---- Two-way sync ------------------------------------------------------

  // Watch the main column for selection changes. Recharge hydrates its widget
  // asynchronously into a shadow root, so wait for the root then observe its
  // subtree (catches Subscribe/One-Time + frequency changes). The mirrored
  // price node is a light-DOM backup signal.
  watchMain() {
    const attach = () => {
      const widget = document.querySelector('recharge-subscription-widget');
      if (!widget || !widget.shadowRoot) return false;
      this.rechargeObserver = new MutationObserver(() => {
        // Re-bind in case Recharge re-rendered the frequency <select>.
        this.attachFrequencyListener();
        this.scheduleReflect();
      });
      this.rechargeObserver.observe(widget.shadowRoot, {
        subtree: true,
        childList: true,
        attributes: true,
      });
      this.attachFrequencyListener();
      return true;
    };
    if (!attach()) {
      this.rechargeReadyObserver = new MutationObserver(() => {
        if (attach()) {
          this.rechargeReadyObserver.disconnect();
          this.scheduleReflect();
        }
      });
      this.rechargeReadyObserver.observe(document.body, {
        subtree: true,
        childList: true,
      });
      // Stop waiting if no widget ever hydrates (non-subscription product) so
      // we don't observe the whole document indefinitely.
      setTimeout(() => this.rechargeReadyObserver?.disconnect(), 15000);
    }
    const priceEl = document.querySelector('.product-single__prices');
    if (priceEl) {
      this.priceObserver = new MutationObserver(() =>
        this.scheduleReflect()
      );
      this.priceObserver.observe(priceEl, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    }
    // Light-DOM fallback: a change on a selling_plan field or the frequency
    // <select> outside the shadow root (widget builds that don't use shadow
    // DOM) still triggers a re-sync.
    document.addEventListener('change', this.boundHandlers.docChange);
  }

  onDocumentChange(e) {
    const target = e.target;
    if (
      target?.matches?.(
        '.rc_widget__option__plans__dropdown, [name="selling_plan"], .js-quantity-selector'
      )
    ) {
      this.scheduleReflect();
    }
  }

  // Recharge's frequency <select> lives in its shadow DOM; a native <select>
  // change fires no DOM mutation, so the subtree observer misses a
  // frequency-only change. Bind a change listener directly (once per element).
  // The deferred follow-up covers Recharge updating the submitted selling_plan
  // just after its own change handler runs.
  attachFrequencyListener() {
    const freq = this.getFrequencySelect();
    if (!freq || this.boundFreqSelects.has(freq)) return;
    this.boundFreqSelects.add(freq);
    freq.addEventListener('change', () => {
      this.scheduleReflect();
      setTimeout(() => this.scheduleReflect(), 250);
    });
  }

  scheduleReflect() {
    if (this.reflectThrottle) return;
    this.reflectThrottle = requestAnimationFrame(() => {
      this.reflectThrottle = null;
      this.reflectFromMain();
    });
  }

  // Read the main column's current selection: variant id, Subscribe vs
  // One-Time, and (for subscriptions) the selected selling plan + frequency
  // label. Returns null if we can't determine the variant.
  readMainSelection() {
    const variantId =
      this.mainVariantInput?.value || this.variantInput?.value;
    if (!variantId) return null;

    let isSubscribe;
    let plan = null;
    let frequencyLabel = null;

    // Primary source: the exact selling_plan the main add-to-cart form would
    // submit (Recharge keeps this light-DOM field updated with the selected
    // purchase option + delivery frequency). Empty value = one-time.
    const mainPlan = this.getMainSellingPlan();
    if (mainPlan !== null) {
      isSubscribe = mainPlan !== '' && mainPlan !== '0';
      if (isSubscribe) plan = mainPlan;
    } else {
      // Fallback: no light-DOM plan field — infer from Recharge's shadow DOM.
      // If we can't find a checked purchase option, the main selection is
      // indeterminate (widget absent or not yet hydrated); return null so we
      // leave the sticky's own selection untouched rather than forcing a type.
      const option = document
        .querySelector('recharge-subscription-widget')
        ?.shadowRoot?.querySelector('.rc-purchase-option input:checked')
        ?.closest('.rc-purchase-option');
      if (!option) return null;
      const text = (option.textContent || '').toLowerCase();
      isSubscribe = !(text.includes('one-time') || text.includes('one time'));
      if (isSubscribe) {
        const opt = this.getFrequencyOption();
        if (opt && /^\d+$/.test(opt.value)) plan = opt.value;
      }
    }

    if (isSubscribe) {
      const opt = this.getFrequencyOption();
      if (opt) frequencyLabel = opt.textContent.trim();
    }

    return { variantId, isSubscribe, plan, frequencyLabel };
  }

  // The selling_plan the main add-to-cart form would serialize: a checked
  // selling_plan radio if Recharge renders a group, else a single/hidden
  // selling_plan input. Returns null when no such field exists (shadow-DOM-
  // only widget), so callers can fall back.
  getMainSellingPlan() {
    if (!this.mainForm) return null;
    const checked = this.mainForm.querySelector(
      'input[name="selling_plan"]:checked'
    );
    if (checked) return checked.value;
    const input = this.mainForm.querySelector('[name="selling_plan"]');
    return input ? input.value : null;
  }

  // Recharge's frequency <select> (in its shadow DOM).
  getFrequencySelect() {
    const root = document.querySelector(
      'recharge-subscription-widget'
    )?.shadowRoot;
    return root?.querySelector('.rc_widget__option__plans__dropdown') || null;
  }

  // The currently-selected frequency option, for the header label and the
  // fallback plan lookup.
  getFrequencyOption() {
    const freq = this.getFrequencySelect();
    return freq?.options?.[freq.selectedIndex] || null;
  }

  // Main -> sticky: activate the dropdown item matching the main selection,
  // adopt the main's exact selling plan (so the sticky adds the right
  // frequency), and reflect the frequency in the header.
  reflectFromMain() {
    if (this.suppress) return;
    const selection = this.readMainSelection();
    if (!selection) return;

    const wantClass = selection.isSubscribe ? 'subscribe' : 'one-time';
    const matches = [...this.dropdownItems].filter(
      (item) => item.dataset.value?.trim() === String(selection.variantId)
    );
    if (!matches.length) return;
    const target =
      matches.find((item) => item.classList.contains(wantClass)) ||
      matches[0];

    if (selection.isSubscribe && selection.plan) {
      // Adopt the main's exact plan so a sticky add uses the same frequency.
      // Ahead of applyStickyItem so its price lookup sees the live plan.
      target.dataset.sellingPlan = selection.plan;
    }

    this.applyStickyItem(target);

    if (selection.isSubscribe && selection.plan) {
      this.sellingPlanInput.value = selection.plan;
    }
    if (selection.isSubscribe && selection.frequencyLabel) {
      this.dropdownHeaderText.textContent = `${this.labels.subscribe} · ${selection.frequencyLabel}`;
    }
  }

  // Sticky -> main: drive the main variant selector and Recharge purchase
  // option to match a sticky choice. Suppresses reflection while our writes
  // settle so they don't echo back.
  pushToMain(item) {
    this.suppress = true;
    // Cancel any pending settle from a previous push; the latest pick wins and
    // the suppress window simply extends rather than releasing early.
    clearTimeout(this.settleTimer);
    try {
      this.setMainVariant(item.dataset.value?.trim());
      this.setRechargePurchaseType(item.classList.contains('subscribe'));
    } catch (err) {
      console.error(err);
    } finally {
      this.settleTimer = setTimeout(() => {
        this.suppress = false;
        // Re-read the main now that our writes have settled, so the sticky
        // adopts the resulting selling_plan (delivery frequency) for its add.
        this.scheduleReflect();
      }, 300);
    }
  }

  // Drive the theme's variant option radios (which sync #product-select and
  // fire Shopify.OptionSelectors -> variant:change -> Recharge re-render).
  setMainVariant(variantId) {
    if (!variantId) return;
    if (String(this.mainVariantInput?.value) === String(variantId)) return;
    const variant = this.getProductVariants()[variantId];
    if (!variant) return;

    let drove = false;
    [variant.option1, variant.option2, variant.option3].forEach(
      (value, idx) => {
        if (value == null) return;
        const group = document.querySelector(
          `.js__pdp-variant-select${idx}`
        );
        if (!group) return;
        const li = [...group.querySelectorAll('li[data-value]')].find(
          (el) => el.getAttribute('data-value') === String(value)
        );
        if (li) {
          li.click();
          drove = true;
        }
      }
    );

    if (!drove && this.mainVariantInput) {
      this.mainVariantInput.value = variantId;
      this.mainVariantInput.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }
  }

  // Click the matching Subscribe / One-Time radio inside Recharge's shadow DOM.
  setRechargePurchaseType(wantSubscribe) {
    const root = document.querySelector(
      'recharge-subscription-widget'
    )?.shadowRoot;
    if (!root) return;
    const options = [...root.querySelectorAll('.rc-purchase-option')];
    const match = options.find((option) => {
      const text = (option.textContent || '').toLowerCase();
      const isOnetime =
        text.includes('one-time') || text.includes('one time');
      return wantSubscribe ? !isOnetime : isOnetime;
    });
    const input = match?.querySelector('input');
    if (input && !input.checked) input.click();
  }

  // Parse (and cache) the on-page product JSON so we can map a variant id to
  // its option values for setMainVariant().
  getProductVariants() {
    if (this._variants) return this._variants;
    this._variants = {};
    try {
      const el = document.querySelector('[id^="ProductJson-"]');
      const product = el ? JSON.parse(el.textContent) : null;
      (product?.variants || []).forEach((v) => {
        this._variants[v.id] = v;
      });
    } catch (err) {
      console.error(err);
    }
    return this._variants;
  }

  // ------------------------------------------------------------------------

  onScroll() {
    if (!this.scrollThrottle) {
      this.scrollThrottle = requestAnimationFrame(() => {
        this.checkScrollPosition();
        this.scrollThrottle = null;
      });
    }
  }

  checkScrollPosition() {
    if (!this.anchorElement) return;
    const rect = this.anchorElement.getBoundingClientRect();
    this.classList.toggle('active', window.scrollY > rect.bottom);
  }

  // Pull the main form's current selling_plan onto the active sticky
  // subscription item immediately before submit, so the cart uses the latest
  // delivery frequency even if live change-detection lagged.
  syncSellingPlanFromMain() {
    const active = this.querySelector(
      '.subscription-dropdown__item.active'
    );
    if (!active || !active.classList.contains('subscribe')) return;
    const plan = this.readMainSelection()?.plan;
    if (plan) {
      active.dataset.sellingPlan = plan;
      if (this.sellingPlanInput) this.sellingPlanInput.value = plan;
    }
  }

  mainQuantity() {
    const qty = parseInt(
      document.querySelector('.js-quantity-selector')?.value,
      10
    );
    return qty > 0 ? qty : 1;
  }

  syncQuantityFromMain() {
    if (!this.quantityInput) return;
    this.quantityInput.value = this.mainQuantity();
  }

  onSubmitHandler(e) {
    e.preventDefault();
    this.closeContent();
    this.syncSellingPlanFromMain();
    this.syncQuantityFromMain();

    const form = e.target;
    const qty = this.mainQuantity();
    window.iwAtc.guardedAdd(
      this.submitButton,
      () => window.iwAtc.add(new FormData(form), qty),
      window.iwAtc.lineFromForm(form)
    );
  }
}

// Safe registration
if (!customElements.get('product-sticky-atc')) {
  customElements.define('product-sticky-atc', ProductStickyAtc);
}
